import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Skeleton,
  Snackbar,
  Alert,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Edit as EditIcon } from "../icons";
import { LocationOn as LocationOnIcon } from "../icons";
import { MoreVert as MoreVertIcon } from "../icons";
import { Phone as PhoneIcon } from "../icons";
import { Delete as DeleteIcon } from "../icons";
import { Save as SaveIcon } from "../icons";
import { Add as AddIcon } from "../icons";
import { Info as InfoIcon } from "../icons";
import { Storefront as StorefrontIcon } from "../icons";
import { PhotoCamera } from "../icons";
import { ShoppingCart as ShoppingCartIcon } from "../icons";
import { Dashboard as DashboardIcon } from "../icons";
import { getFirestore, collection, query, getDocs, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { auth, uploadImage } from "../firebase";
import { T } from "./Translate";
import StoresAppBar from "./stores/StoresAppBar";
import { brandColors } from "../theme";
import { geocodeAddress } from "../utils/geo";
import { onAccent } from "./supportChatStyles";

// Accent de marca: se oscurece en Modo Claro para seguir siendo legible.
const ACCENT = brandColors.accent;
const BASE_URL = "notiline.com/stores/";

// blob: URLs sólo son válidas en la pestaña que las creó; si quedaron
// guardadas en Firestore de una carga anterior, no deben usarse como src.
const safeLogoUrl = (url) => (url && !url.startsWith("blob:") ? url : undefined);

const MyStores = () => {
  const [stores, setStores] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: "", description: "", address: "", phone: "", logoUrl: "" });
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, id: null });
  const [detailsStore, setDetailsStore] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, store: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (userAuth) => {
      if (userAuth) {
        setUser(userAuth);
        fetchStores(userAuth.uid);
      } else {
        navigate("/login");
      }
    });
    return unsubscribe;
  }, [navigate]);

  const fetchStores = async (uid) => {
    setLoading(true);
    try {
      const db = getFirestore();
      // Obtener todos los documentos de la colección 'stores' y filtrar por ownerId
      const q = query(collection(db, "stores"));
      const snap = await getDocs(q);
      // Filtrar por ownerId y asegurar que se use el id (nombre del doc) como fallback de nombre
      setStores(
        snap.docs
          .filter((d) => d.data().ownerId === uid)
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              // Si no hay campo name, usar el id del documento como nombre
              name: data.storeName || data.name || d.id,
              slug: data.slug || d.id,
            };
          })
      );
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al cargar tiendas</T>, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (store) => {
    setEditingId(store.id);
    setEditData({
      name: store.name || "",
      description: store.description || "",
      address: store.address || "",
      phone: store.phone || "",
      logoUrl: store.logoUrl || "",
    });
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleSave = async (id) => {
    const name = editData.name.trim();
    if (!name) {
      setSnackbar({ open: true, message: <T>Ingresa un nombre para tu tienda.</T>, severity: "error" });
      return;
    }
    try {
      const db = getFirestore();
      // La página pública y el directorio muestran storeName. El slug no se
      // edita: es el ID del documento y las reglas de Firestore lo rechazan.
      const updates = { ...editData, name, storeName: name };
      // Si cambió la dirección, recalcular la ubicación en el mapa.
      const previous = stores.find((s) => s.id === id);
      if (editData.address && editData.address !== previous?.address) {
        const coords = await geocodeAddress(editData.address);
        if (coords) Object.assign(updates, coords);
      }
      await updateDoc(doc(db, "stores", id), updates);
      setEditingId(null);
      fetchStores(user.uid);
      setSnackbar({ open: true, message: <T>Tienda actualizada</T>, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al guardar cambios</T>, severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    setConfirmDelete({ open: true, id });
  };

  const confirmDeleteStore = async () => {
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, "stores", confirmDelete.id));
      fetchStores(user.uid);
      setSnackbar({ open: true, message: <T>Tienda eliminada</T>, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al eliminar tienda</T>, severity: "error" });
    } finally {
      setConfirmDelete({ open: false, id: null });
    }
  };

  const handleToggleActive = async (store) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, "stores", store.id), { active: !store.active });
      fetchStores(user.uid);
      setSnackbar({ open: true, message: store.active ? <T>Tienda desactivada</T> : <T>Tienda activada</T>, severity: "info" });
    } catch (err) {
      setSnackbar({ open: true, message: <T>Error al cambiar estado</T>, severity: "error" });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      setEditData((prev) => ({ ...prev, logoUrl: url }));
    } catch (err) {
      setSnackbar({ open: true, message: <T>No pudimos subir la imagen</T>, severity: "error" });
    }
  };

  const editingStore = stores.find((s) => s.id === editingId);

  const openMenu = (event, store) => setMenu({ anchor: event.currentTarget, store });
  const closeMenu = () => setMenu({ anchor: null, store: null });
  const menuAction = (action) => {
    const { store } = menu;
    closeMenu();
    action(store);
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <StoresAppBar title={<T>Mis Tiendas</T>} subtitle={<T>Gestiona tus enlaces activos</T>} maxWidth="md" />
      <Box sx={{ maxWidth: { xs: "100%", md: "980px" }, mx: "auto", mt: 2, p: 2, pb: 12, boxSizing: "border-box" }}>
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {[0, 1].map((i) => (
              <Skeleton key={i} variant="rounded" height={210} sx={{ borderRadius: "18px" }} />
            ))}
          </Box>
        ) : (
          <>
            {stores.length === 0 && (
              <Box sx={{ textAlign: "center", py: 7, color: "text.secondary" }}>
                <StorefrontIcon sx={{ fontSize: 44, opacity: 0.6, mb: 1 }} />
                <Typography sx={{ color: "text.secondary" }}><T>Todavía no tenés tiendas.</T></Typography>
              </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
              {stores.map((store) => (
                <Card
                  key={store.id}
                  elevation={0}
                  sx={{
                    bgcolor: "background.paper",
                    backgroundImage: "none",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: "18px",
                  }}
                >
                  <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                      <Avatar
                        src={safeLogoUrl(store.logoUrl)}
                        sx={{ bgcolor: alpha(ACCENT, 0.14), color: ACCENT, width: 52, height: 52, flexShrink: 0 }}
                      >
                        <StorefrontIcon />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "1.02rem", fontWeight: 700, lineHeight: 1.3, wordBreak: "break-word" }}>
                          {store.name}
                        </Typography>
                        {store.slug && (
                          <Typography
                            component="a"
                            href={`https://${BASE_URL}${store.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: "block", fontSize: "0.78rem", color: ACCENT, textDecoration: "none", wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}
                          >
                            {BASE_URL}{store.slug}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" aria-label="Más opciones" onClick={(e) => openMenu(e, store)} sx={{ color: "text.secondary", flexShrink: 0 }}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {store.description && (
                      <Typography
                        sx={{
                          mt: 1.25,
                          fontSize: "0.85rem",
                          lineHeight: 1.45,
                          color: "text.secondary",
                          wordBreak: "break-word",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {store.description}
                      </Typography>
                    )}

                    {(store.address || store.phone) && (
                      <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.375 }}>
                        {store.address && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, color: "text.secondary", minWidth: 0 }}>
                            <LocationOnIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{store.address}</Typography>
                          </Box>
                        )}
                        {store.phone && (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, color: "text.secondary", minWidth: 0 }}>
                            <PhoneIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                            <Typography noWrap sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{store.phone}</Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box
                      sx={{
                        mt: 1.25,
                        py: 0.25,
                        pl: 1.25,
                        pr: 0.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        borderRadius: "12px",
                        bgcolor: "action.hover",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: store.active ? "success.main" : "text.disabled" }} />
                        <Typography sx={{ fontSize: "0.82rem", fontWeight: 600 }}>
                          {store.active ? <T>Activa</T> : <T>Inactiva</T>}
                        </Typography>
                      </Box>
                      <Switch
                        checked={!!store.active}
                        onChange={() => handleToggleActive(store)}
                        inputProps={{ "aria-label": "Activar tienda" }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: ACCENT },
                        }}
                      />
                    </Box>

                    <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<ShoppingCartIcon />}
                        onClick={() => navigate(`/stores/${store.slug}`, { state: { tab: "productos" } })}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 999,
                          boxShadow: "none",
                          bgcolor: ACCENT,
                          color: onAccent,
                          "&:hover": { bgcolor: ACCENT, opacity: 0.88, boxShadow: "none" },
                        }}
                      >
                        <T>Productos</T>
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DashboardIcon />}
                        onClick={() => navigate(`/stores/${store.slug}`, { state: { tab: "panel" } })}
                        sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, color: ACCENT, borderColor: alpha(ACCENT, 0.5) }}
                      >
                        <T>Panel</T>
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Button
              fullWidth
              startIcon={<AddIcon />}
              onClick={() => navigate("/stores/create")}
              variant="outlined"
              sx={{ py: 1, color: ACCENT, borderColor: alpha(ACCENT, 0.5), borderStyle: "dashed", borderRadius: "14px", textTransform: "none", fontWeight: 700 }}
            >
              <T>Agregar Tienda</T>
            </Button>
          </>
        )}

        <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}>
          <MenuItem onClick={() => menuAction(setDetailsStore)}>
            <ListItemIcon><InfoIcon fontSize="small" /></ListItemIcon>
            <T>Ver detalles</T>
          </MenuItem>
          <MenuItem onClick={() => menuAction(handleEdit)}>
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <T>Editar</T>
          </MenuItem>
          <MenuItem onClick={() => menuAction((store) => handleDelete(store.id))} sx={{ color: "error.main" }}>
            <ListItemIcon sx={{ color: "error.main" }}><DeleteIcon fontSize="small" /></ListItemIcon>
            <T>Eliminar</T>
          </MenuItem>
        </Menu>

        <Dialog open={!!editingId} onClose={() => setEditingId(null)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "18px", backgroundImage: "none" } }}>
          <DialogTitle sx={{ fontWeight: 700 }}>{editingStore?.name}</DialogTitle>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
              <Avatar src={safeLogoUrl(editData.logoUrl)} sx={{ width: 52, height: 52, bgcolor: alpha(ACCENT, 0.14), color: ACCENT }}>
                <StorefrontIcon />
              </Avatar>
              <Button variant="outlined" component="label" startIcon={<PhotoCamera />} sx={{ textTransform: "none", borderRadius: 999, color: ACCENT, borderColor: alpha(ACCENT, 0.5) }}>
                <T>Subir Logo</T>
                <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
              </Button>
            </Box>
            <TextField size="small" label={<T>Nombre</T>} name="name" value={editData.name} onChange={handleEditChange} fullWidth />
            <TextField size="small" label={<T>Descripción</T>} name="description" value={editData.description} onChange={handleEditChange} fullWidth multiline minRows={2} />
            <TextField size="small" label={<T>Dirección</T>} name="address" value={editData.address} onChange={handleEditChange} fullWidth />
            <TextField size="small" label={<T>Teléfono</T>} name="phone" value={editData.phone} onChange={handleEditChange} fullWidth />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setEditingId(null)} color="inherit" sx={{ textTransform: "none" }}>
              <T>Descartar cambios</T>
            </Button>
            <Button
              startIcon={<SaveIcon />}
              onClick={() => handleSave(editingId)}
              variant="contained"
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999, boxShadow: "none", bgcolor: ACCENT, color: onAccent, "&:hover": { bgcolor: ACCENT, opacity: 0.88, boxShadow: "none" } }}
            >
              <T>Guardar</T>
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
        <Dialog open={!!detailsStore} onClose={() => setDetailsStore(null)} fullWidth maxWidth="xs">
          <DialogTitle sx={{ color: ACCENT, fontWeight: 700 }}>{detailsStore?.name}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}>
              {detailsStore?.description && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Descripción</T></Typography>
                  <Typography variant="body2">{detailsStore.description}</Typography>
                </Box>
              )}
              {detailsStore?.address && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Dirección</T></Typography>
                  <Typography variant="body2">{detailsStore.address}</Typography>
                </Box>
              )}
              {detailsStore?.phone && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Teléfono</T></Typography>
                  <Typography variant="body2">{detailsStore.phone}</Typography>
                </Box>
              )}
              {detailsStore?.slug && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Enlace</T></Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{BASE_URL}{detailsStore.slug}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Estado</T></Typography>
                <Typography variant="body2">{detailsStore?.active ? <T>Activa</T> : <T>Inactiva</T>}</Typography>
              </Box>
              {detailsStore?.createdAt && (
                <Box>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}><T>Creada el</T></Typography>
                  <Typography variant="body2">{new Date(detailsStore.createdAt.seconds * 1000).toLocaleDateString()}</Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsStore(null)}><T>Cerrar</T></Button>
          </DialogActions>
        </Dialog>
        <Dialog open={confirmDelete.open} onClose={() => setConfirmDelete({ open: false, id: null })}>
          <DialogTitle><T>¿Eliminar tienda?</T></DialogTitle>
          <DialogContent>
            <DialogContentText><T>Esta acción no se puede deshacer.</T></DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDelete({ open: false, id: null })}><T>Cancelar</T></Button>
            <Button onClick={confirmDeleteStore} color="error"><T>Eliminar</T></Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MyStores;
